import { isNightclubBooking, type NightclubInput } from "./booking-flow";

/** The package fields that decide whether a club sells it as a table. */
export interface ClubTableShape {
  typePrice?: number | null;
  maxGuests?: number | null;
}

/**
 * Is this club package a TABLE, one price for the whole party?
 *
 * `typePrice` 2 (per night) or 3 (per group) is a table by its price type.
 * Seating for more than one guest is a table too, and that fallback is the
 * case that actually happens: Leone's tables have no price type set in older
 * database copies, so they read as per-person (type 0) with `maxGuests` above
 * one. Anything else is a per-person entry ticket.
 *
 * Only meaningful inside a club flow. The website's club sheet and the app's
 * club step call it after `isNightclubBooking` has already sent the guest
 * there; the API, which sees every activity, goes through
 * `isClubTablePackage` below so a quad bike for two is never read as a table.
 */
export function isClubTable(pkg: ClubTableShape | null | undefined): boolean {
  if (!pkg) return false;
  const type = pkg.typePrice ?? 0;
  return type === 2 || type === 3 || (pkg.maxGuests ?? 1) > 1;
}

/**
 * The server's form of the same rule: the activity books a night
 * (`isNightclubBooking`, the same gate both clients open their club flow on)
 * AND the package is a table (`isClubTable`).
 *
 * A club table is priced once for the party, never times heads, and the
 * night's ticket price never replaces it: that price is a per-person entry.
 */
export function isClubTablePackage(
  pkg: ClubTableShape | null | undefined,
  activity: NightclubInput,
): boolean {
  return isNightclubBooking(activity) && isClubTable(pkg);
}
